import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BehaviorSubject,
  filter,
  Subscription,
  take,
  tap,
} from 'rxjs';
import { PlannedPurpose } from 'src/app/core/models/purpose';
import { Mongo } from 'src/app/core/models/mongo';
import { isQualitativeKpi, isQuantitativeKpi } from '../../utils/shared';
import { AskConfirmationDialogComponent } from '../ask-confirmation-dialog/ask-confirmation-dialog.component';
import { AddActionDialogComponent } from './add-action-dialog.component';
import { AddKpiDialogComponent } from './add-kpi-dialog.component';
import { PlanningTableActionDialogComponent } from './edit-action-dialog.component';
import { PlanningTableKpiDialogComponent } from './edit-kpi-dialog.component';
import { EntityDeleteConfirmationDialogComponent } from '../entity-delete-confirmation-dialog/entity-delete-confirmation-dialog.component';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

interface Indexes {
  actionIndex: number;
  kpiIndex: number;
}

@Component({
    selector: 'app-planning-table[selectedBenefitId][plannedPurposes]',
    templateUrl: './planning-table.component.html',
    styles: [
        '.mat-mdc-row td:nth-child(-n+2) {vertical-align: top }',
        '.mat-mdc-row td:last-child { padding: 0 0.7em!important }',
        '.mat-column-progress, .mat-column-kpi, .mat-column-action, .mat-column-target, .mat-column-results { text-align: center;}',
        ' .mat-column-kpi, .mat-column-action { width: 38%; padding-left: 1em}',
        '.mat-column-progress, .mat-column-target, .mat-column-results { max-width: 10%}',
        '.cell-title {min-height: 40px}',
        `
    /* TODO(mdc-migration): The following rule targets internal classes of progress-bar that may no longer apply for the MDC version. */
    mat-progress-bar { min-height: 0.7em!important; }`,
        '.mat-mdc-row td, th {border-collapse: collapse;  border: 1px solid hsl(0deg 0% 88%);}',
    ],
    imports: [MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatIconButton, MatIcon, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, TranslateModule]
})
export class PlanningTableComponent implements OnInit, OnChanges, OnDestroy {
  private dialog = inject(MatDialog);

  // hides CTAs
  readonly readOnly = input.required<boolean>();

  readonly plannedPurposes = input<PlannedPurpose[]>([]);

  readonly selectedBenefitId = input.required<Mongo.ObjectId>();

  readonly benefitChange = output<PlannedPurpose[]>();

  actions: PlannedPurpose.Action[] = [];

  actionIndexes$: BehaviorSubject<Indexes[]> = new BehaviorSubject<Indexes[]>(
    [] as Indexes[]
  );

  tableCols = ['action', 'kpi', 'progress'];

  iconSize = '1.4rem';

  isQualitativeKpi = isQualitativeKpi;
  isQuantitativeKpi = isQuantitativeKpi;

  private sub!: Subscription;

  ngOnInit(): void {
    const indexOfSelectedBenefit = this.plannedPurposes().findIndex(
      (benefit) => benefit._id.$oid === this.selectedBenefitId().$oid
    );
    indexOfSelectedBenefit !== -1
      ? (this.actions = this.plannedPurposes()[indexOfSelectedBenefit].actions)
      : [];
    this.actionIndexes$.next(this.flattenActions(this.actions));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes?.['selectedBenefitId']?.currentValue) {
      const {
        selectedBenefitId: { currentValue: currentBenefit },
      } = changes;
      const indexOfSelectedBenefit = this.plannedPurposes().findIndex(
        (benefit) => benefit._id.$oid === currentBenefit.$oid
      );
      indexOfSelectedBenefit !== -1
        ? (this.actions = this.plannedPurposes()[indexOfSelectedBenefit].actions)
        : [];
      this.actionIndexes$.next(this.flattenActions(this.actions));
    }
    if(changes?.['PlannedPurposes']?.currentValue) {
      const PlannedPurposes = changes['PlannedPurposes'].currentValue as PlannedPurpose[];
      const indexOfSelectedBenefit = this.plannedPurposes().findIndex(
        b => b._id.$oid === this.selectedBenefitId().$oid
      );
      indexOfSelectedBenefit !== -1
      ? this.actions = PlannedPurposes[indexOfSelectedBenefit].actions
      : [];
      this.actionIndexes$.next(this.flattenActions(this.actions))
    }
  }

  asQuantitativeKpi(kpi: any): PlannedPurpose.QuantitativeKpi {
    // return kpi as PlannedPurpose.QuantitativeKpi;
    return new PlannedPurpose.QuantitativeKpi(kpi.title, kpi.description, kpi.progress, kpi.target);
  }

  asQualitativeKpi(kpi: any): PlannedPurpose.QualitativeKpi {
    return new PlannedPurpose.QualitativeKpi(kpi.title, kpi.description, kpi.progres);
  }

  get selectedBenefit(): PlannedPurpose | null {
    const selectedBenefitIndex = this.plannedPurposes().findIndex(
      (benefit) => this.selectedBenefitId() === benefit._id
    );
    return selectedBenefitIndex !== -1
      ? this.plannedPurposes()[selectedBenefitIndex]
      : null;
  }

  private flattenActions(actions: PlannedPurpose.Action[]): Indexes[] {
    return actions.flatMap((action, actionIndex) => {
      return action.kpis.length
        ? action.kpis.map((_, kpiIndex) => ({ kpiIndex, actionIndex }))
        : { actionIndex, kpiIndex: -1 };
    });
  }

  editAction(selectedAction: Indexes) {
    const action = this.actions[selectedAction.actionIndex];
    this.dialog
      .open(PlanningTableActionDialogComponent, {
        data: {
          action,
          benefitList: this.plannedPurposes().map((benefit) =>
            Object.assign({}, { _id: benefit._id, title: benefit.title })
          ),
          currentBenefit: this.selectedBenefitId(),
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((data) => !!data),
        tap(
          (dialogData: {
            benefitId: Mongo.ObjectId;
            description: string;
            title: string;
            _id: Mongo.ObjectId,
          }) => {
            // If benefitId is not the same as the selectedBenefit move the action in the benefit selected by the user
            if (dialogData.benefitId !== this.selectedBenefitId()) {
              const sourceBenefitIndex = this.plannedPurposes().findIndex(
                (benefit) => benefit._id === this.selectedBenefitId()
              );
              const targetBenefitIndex = this.plannedPurposes().findIndex(
                (benefit) => benefit._id === dialogData.benefitId
              );
              const actionToMove = this.plannedPurposes()[
                sourceBenefitIndex
              ].actions.splice(selectedAction.actionIndex, 1);
              this.plannedPurposes()[targetBenefitIndex].actions.push(
                ...actionToMove
              );
            } else {
              this.actions.splice(selectedAction.actionIndex, 1, {
                _id: dialogData._id,
                description: dialogData.description,
                title: dialogData.title,
                kpis: [...action.kpis],
              });
            }
            this.actionIndexes$.next(this.flattenActions(this.actions));
            this.benefitChange.emit(this.plannedPurposes());
          }
        )
      )
      .subscribe();
  }

  removeAction(selectedAction: Indexes) {
    const action = this.actions[selectedAction.actionIndex];

    this.dialog
      .open(AskConfirmationDialogComponent, {
        data: {
          resource: action.title,
          resourceName: 'ACTION',
          type: 'PLANNING'
        },
      })
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        tap(() => {
          this.actions.splice(selectedAction.actionIndex, 1);
          this.actionIndexes$.next(this.flattenActions(this.actions));
          this.benefitChange.emit(this.plannedPurposes());
        })
      )
      .subscribe();
  }

  editKpi(action: Indexes) {
    const kpis = this.actions[action.actionIndex].kpis;

    this.dialog
      .open(PlanningTableKpiDialogComponent, {
        data: kpis[action.kpiIndex],
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((data) => !!data),
        tap((data) => {
          kpis.splice(action.kpiIndex, 1, data);
          this.actionIndexes$.next(this.flattenActions(this.actions));
          this.benefitChange.emit(this.plannedPurposes());
        })
      )
      .subscribe();
  }

  removeKpi(action: Indexes) {
    const kpis = this.actions[action.actionIndex].kpis;
    this.dialog
      .open(EntityDeleteConfirmationDialogComponent, {
        data: {
          title: "Vuoi eliminare questo KPI?"
        }
      })
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        tap(() => {
          kpis.splice(action.kpiIndex, 1);
          this.actionIndexes$.next(this.flattenActions(this.actions));
          this.benefitChange.emit(this.plannedPurposes());
        })
      )
      .subscribe();
  }

  addAction() {
    this.dialog
      .open(AddActionDialogComponent)
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        tap(({ _id, title, description }) => {
          console.log('[Planning/Add Action] data', _id, title, description)
          const action: PlannedPurpose.Action = {
            _id,
            title,
            description,
            kpis: [],
          };
          this.selectedBenefit?.actions.push(action);
          this.actionIndexes$.next(this.flattenActions(this.actions));
          this.benefitChange.emit(this.plannedPurposes());
        })
      )
      .subscribe();
  }

  addKpi() {
    this.dialog
      .open(AddKpiDialogComponent, {
        data: this.selectedBenefit?.actions,
      })
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        tap(({ actionIndex, kpi }) => {
          let mappedKpi: PlannedPurpose.QuantitativeKpi | PlannedPurpose.QualitativeKpi;
          if(isQuantitativeKpi(kpi)) {
            const temp = kpi as PlannedPurpose.QuantitativeKpi; 
            mappedKpi = new PlannedPurpose.QuantitativeKpi(temp.title, temp.description, temp.progress, temp.target);
          } else {
            const temp = kpi as PlannedPurpose.QualitativeKpi
            mappedKpi = new PlannedPurpose.QualitativeKpi(temp.title, temp.description, temp.progress);
          }
          this.selectedBenefit?.actions[actionIndex].kpis.push(mappedKpi);
          this.actionIndexes$.next(this.flattenActions(this.actions));
          this.benefitChange.emit(this.plannedPurposes());
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
